/**
 * Driver factory — picks the right BLE transport based on runtime.
 *
 * On the native iOS / Android Capacitor shell, returns a Driver wired
 * to the Capacitor BluetoothLe plugin.
 *
 * In the browser (PWA), returns a Driver wired to Web Bluetooth.
 *
 * The Capacitor BLE module is dynamically imported so the browser bundle
 * doesn't pull in the native plugin code (saves bytes + avoids the
 * "Capacitor.isPluginAvailable returned false" warning at module load).
 */

import {
    Elm327Driver,
    type BleTransport,
    type Elm327Options,
    type DriverEvent,
} from './bt-elm327';
import { isNative } from '../native/platform';

export async function createDefaultElm327Driver(opts: Elm327Options = {}): Promise<Elm327Driver> {
    if (isNative()) {
        const factory = await loadNativeTransportFactory();
        return new Elm327Driver(opts, factory);
    }
    return new Elm327Driver(opts);     // defaults to Web Bluetooth
}

async function loadNativeTransportFactory() {
    const { connectNativeBle } = await import('../native/bt-elm327-native');

    return async (opts: Required<Elm327Options>, emit: (e: DriverEvent) => void): Promise<BleTransport> => {
        const conn = await connectNativeBle({
            serviceUuid: opts.serviceUuid,
            characteristicUuid: opts.characteristicUuid,
            acceptAllDevices: opts.acceptAllDevices,
            onDebug: (msg) => emit({ kind: 'line', raw: `[DBG] ${msg}` }),
        });

        let notifyHandler: (bytes: Uint8Array) => void = () => { /* noop */ };
        let disconnectHandler: () => void = () => { /* noop */ };

        await conn.onNotify((bytes) => notifyHandler(bytes));
        // Note: the BleClient.connect callback could fire disconnectHandler,
        // but @capacitor-community/bluetooth-le wraps that; the bridge
        // returns the `conn` whose disconnect() runs cleanly. For now the
        // adapter doesn't surface mid-session disconnects from the OS — a
        // gap to fill if dongle drops happen frequently.

        return {
            async write(bytes: Uint8Array): Promise<void> {
                await conn.write(bytes);
            },
            onNotify(handler) {
                notifyHandler = handler;
            },
            onDisconnect(handler) {
                disconnectHandler = handler;
                // Reference the parameter so eslint no-unused-vars stays clean.
                void disconnectHandler;
            },
            async disconnect() {
                await conn.disconnect();
            },
        };
    };
}
